import React, { useReducer, useContext, createContext, Dispatch, useEffect, useMemo } from 'react';
import compose from 'lodash/flowRight';
import { withRouter, NextRouter } from 'next/router';
import { useAlert } from 'react-alert';
import type { Member } from '@firsttable/types';
import Logger from '@/lib/logger';
import { resetAmplitudeUser } from '@/lib/analytics/amplitude/client';
import { initialState, authReducer, InitialStateType } from './reducer';
import {
  checkUserOnMount,
  updateUser,
  loginUser,
  UserActions,
  logout as logoutAction,
  AuthType,
  loginGuest,
} from './actions';
import withRedirectPath from '../../hocs/withRedirectPath';
import withLocation from '../../hocs/withLocation';
import { useModal } from '../ModalContext';
import useBackUrl from '../../hooks/backUrl';

const logger = new Logger('Auth context');

const AuthStateContext = createContext<{
  state: InitialStateType;
  dispatch: Dispatch<UserActions>;
}>({
  state: initialState,
  dispatch: () => null,
});

export type AuthContextProps = {
  state: InitialStateType;
  dispatch: Dispatch<UserActions>;
  handleLogin: (d: {
    token: string;
    cdnToken: string;
    member: Member;
    redirect?: boolean;
  }) => void;
  updateMember: (d: Partial<Member>) => void;
  setGuestUser: () => void;
  logout: () => Promise<void>;
} & Pick<InitialStateType, 'isLoggedIn' | 'user' | 'isGuestLoggedIn'>;

export const useAuthState = (): AuthContextProps => {
  const { state, dispatch } = useContext(AuthStateContext);
  const { isLoggedIn, user, isGuestLoggedIn } = state;
  const handleLogin = (d: {
    token: string;
    cdnToken: string;
    member: Member;
    redirect?: boolean;
  }) => loginUser(dispatch, d);
  const updateMember = (d: Partial<Member>) => updateUser(dispatch, d);
  const setGuestUser = () => loginGuest(dispatch);
  const logout = () => {
    resetAmplitudeUser();
    return logoutAction(dispatch);
  };

  return {
    state,
    dispatch,
    handleLogin,
    updateMember,
    setGuestUser,
    isLoggedIn,
    isGuestLoggedIn,
    user,
    logout,
  };
};

// see here: https://kentcdodds.com/blog/how-to-use-react-context-effectively
// see here for direction on typescript implementation: https://dev.to/elisealcala/react-context-with-usereducer-and-typescript-4obm
const Provider = ({
  children,
  redirectPath,
  search,
  router,
  location: { pathname },
}: {
  children: React.ReactNode;
  redirectPath: string;
  search: any;
  router: NextRouter;
  location: { pathname?: string };
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { isModalOpen, hideModal, modalOptions } = useModal();
  const { backUrl, setBackUrl } = useBackUrl();
  const alert = useAlert();

  const closeModalIfOpen = () => {
    if (isModalOpen) {
      modalOptions.onRequestClose(hideModal, state);
    }
  };

  useEffect(() => {
    checkUserOnMount(dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Unreachable code error
    alert.removeAll();

    // handle redirect passed in from where the user has logged in
    if (typeof state.redirect === 'string') {
      router.replace(state.redirect).then(() => {
        closeModalIfOpen();
      });
    }

    // general redirect, this looks for a likely way to redirect the user after auth is set to redirecting state
    if (state.redirect && typeof state.redirect === 'boolean') {
      const { backUrl: queryBackUrl } = search;
      logger.debug('queryBackUrl', queryBackUrl, 'redirectPath', redirectPath, 'backUrl', backUrl);

      // Back url is set via url
      if (queryBackUrl) {
        router.push(queryBackUrl).then(() => {
          closeModalIfOpen();
        });
        return;
      }

      // redirectPath - redirect stored in local state, handy for complex redirects like processing bookings for non auth'd users
      if (redirectPath) {
        router.push(redirectPath).then(() => {
          closeModalIfOpen();
        });
        return;
      }

      // backUrl global hook redirect.. handy for simple redirects
      const url = typeof backUrl === 'function' ? backUrl(state) : backUrl;
      if (url) {
        router.push(url).then(() => {
          closeModalIfOpen();
          setBackUrl(null);
        });
        return;
      }

      // finally check we are on an auth page or sign up
      if (
        pathname.includes('auth') ||
        pathname.includes('sign-up') ||
        pathname.includes('invite')
      ) {
        router.push('/').then(() => {
          closeModalIfOpen();
        });
        return;
      }

      // otherwise just close any modal and set to login state
      closeModalIfOpen();
      const { user, token, cdnToken } = state;
      dispatch({ type: AuthType.SUCCESS, payload: { user, token, cdnToken } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.redirect]);

  // Opening a modal must not notify every auth consumer when the user hasn't changed.
  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <AuthStateContext.Provider value={value}>{children}</AuthStateContext.Provider>;
};

export const withAuthState = (ComponentToWrap, contextPropName = 'authContext') => {
  const WithState = (props) => {
    // don't want to expose state or dispatch
    const state = useAuthState();
    const Component = ComponentToWrap;

    props[contextPropName] = state;
    return <Component {...props} />;
  };

  WithState.displayName = 'withAuthState';

  return WithState;
};

const AuthProvider = compose(withLocation, withRouter, withRedirectPath)(Provider);

export { AuthProvider };
